import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import type { GeoJsonFeature } from '../../services/ColombiaMapService';
import { POPULATION_DATA, SERVICE_DATA } from '../../shared/population-colors';
import './ViewDetail.css';

type Props = {
  item: GeoJsonFeature;
  onClose: () => void;
  onGeneratePdf: (item: GeoJsonFeature) => void;
  onCopyUrl: (id: string) => void;
  capitalizarPrimeraLetra: (s: string) => string;
};

const ViewDetail: React.FC<Props> = ({
  item,
  onClose,
  onGeneratePdf,
  onCopyUrl,
  capitalizarPrimeraLetra,
}) => {
  const [diasDeAtencionSet, setDiasDeAtencionSet] = useState<Set<string>>(new Set());
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const infoRef = useRef<HTMLDivElement | null>(null);
  const [tipPos, setTipPos] = useState<{left:number; top:number} | null>(null);

  const p = item.properties ?? {};

  // helpers
  const get = (k: string) => String(p[k] ?? '').trim();
  const has = (k: string) => {
    const v = get(k).toLowerCase();
    return v !== '' && v !== '0' && v !== 'n/a' && v !== 'na' && v !== 'null' && v !== 'sin información';
  };

  // flags
  const hasDireccion = has('Domicilios completos');
  const hasMuniDepto = has('MUNICIPIO.1');
  const hasSigep     = has('COD SIGEP');
  const hasCiiu      = has('Código CIIU');
  const hasTel       = has('Teléfono del Centro de Cuidado');
  const hasEmail     = has('Correo electrónico del Centro de Cuidado');
  const hasWeb       = has('Página web del Centro de Cuidado');

  const hasHoraIni = has('Horario de inicio de la atención');
  const hasHoraFin = has('Horario de finalización de la atención');

  const accSilla  = p['Accesibilidad sillas de ruedas'] !== 'No' && p['Accesibilidad sillas de ruedas'] !== undefined;
  const accVisual = p['Accesibilidad personas ciegas'] !== 'No' && p['Accesibilidad personas ciegas'] !== undefined;
  const hasAcc    = accSilla || accVisual;

  const dayOfWeek = [
    { dia: "Lunes", inicial: "L" },
    { dia: "Martes", inicial: "M" },
    { dia: "Miércoles", inicial: "M" },
    { dia: "Jueves", inicial: "J" },
    { dia: "Viernes", inicial: "V" },
    { dia: "Sábado", inicial: "S" },
    { dia: "Domingo", inicial: "D" },
  ];

  useEffect(() => {
    if (has('Días de atención')) {
      const dias = get('Días de atención').split(',').map(d => d.trim()).filter(Boolean);
      setDiasDeAtencionSet(new Set(dias));
    } else {
      setDiasDeAtencionSet(new Set());
    }
  }, [p]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDiaDeAtencion = (dia: string): boolean => diasDeAtencionSet.has(dia);
  const hasDias = diasDeAtencionSet.size > 0;

  useEffect(() => {
    if (!tooltipVisible || !infoRef.current) return;
    const r = infoRef.current.getBoundingClientRect();
    setTipPos({ left: r.left + r.width / 2, top: r.top });
  }, [tooltipVisible]);


  const buildPopulation = useMemo(() => {
    const name = (String(p['Población atendida'] ?? '')).toLowerCase();
    const arr: { name: string; color: string; icon: string; label: string }[] = [];
    if (name.includes("mayores")) arr.push(POPULATION_DATA["Personas Mayores"]);
    if (name.includes("niños")) arr.push(POPULATION_DATA["Niños, Niñas y/o Adolescentes"]);
    if (name.includes("discapacidad")) arr.push(POPULATION_DATA["Personas con Discapacidad"]);
    if (name.includes("cuidador")) arr.push(POPULATION_DATA["Cuidadores"]);
    return arr;
  }, [p]);

  const buildService = useMemo(() => {
    const name = (String(p['Servicio Ofrecido'] ?? '')).toLowerCase();
    if (!name) return [];
    const arr: { name: string; label: string }[] = [];
    if (name.includes("centro de desarrollo infantil")) arr.push(SERVICE_DATA["Centro de Desarrollo Infantil"]);
    if (name.includes("jardín") || name.includes("guardería")) arr.push(SERVICE_DATA["Jardín / Guardería Infantil"]);
    if (name.includes("escuela") || name.includes("colegio")) arr.push(SERVICE_DATA["Escuela o Colegio"]);
    if (name.includes("limpieza")) arr.push(SERVICE_DATA["Relevos en la limpieza de espacios"]);
    if (name.includes("día")) arr.push(SERVICE_DATA["Cuidado durante el día - Centro Día"]);
    if (name.includes("lavandería")) arr.push(SERVICE_DATA["Lavandería"]);
    if (name.includes("residencia")) arr.push(SERVICE_DATA["Residencia"]);
    if (name.includes("comedor")) arr.push(SERVICE_DATA["Comedor Comunitario"]);
    if (name.includes("transporte de poblaciones")) arr.push(SERVICE_DATA["Transporte de poblaciones específicas"]);
    return arr;
  }, [p]);

  const notaUbicacion = String(p["NOTA UBICACIÓN"] ?? "");
  const showNota = notaUbicacion.length > 0;

  const muniTxt = String(p["MUNICIPIO.1"] ?? "");
  const [municipio, departamento] = muniTxt.split(",").map(s => s?.trim?.() ?? "");

  return (
    <div className="flex flex-col w-full h-96 md:h-[calc(100vh-100px)] pb-4">
      {/* volver */}
      <div className="pb-4 pt-4 px-6">
        <button
          onClick={onClose}
          className="flex items-center text-primary hover:text-primary/80 transition-colors"
        >
          <i className="material-icons-outlined text-primary mr-1">arrow_back_ios</i>
        
        </button>
      </div>

      {/* header igual a lista */}
      <div className="flex justify-between items-start bg-primaryHeader px-2 md:px-6 py-2 font-semibold">
        <p className="text-base md:text-base leading-6 text-black">
          {p['Nombre'] ?? '-'}
        </p>
        <div className="flex items-center ml-6">
          <button className="p-1 flex items-center" onClick={() => onGeneratePdf(item)}>
            <i className="material-icons-outlined text-accent">save_alt</i>
          </button>
          <div className="h-8 border mx-2 border-accent" />
          <button className="p-1 flex items-center" onClick={() => onCopyUrl(String(p.ID ?? ''))}>
            <i className="material-icons-outlined text-accent">share</i>
          </button>
        </div>
      </div>

      {/* contenido */}
      <div className="px-4 md:px-12 overflow-y-auto pb-4 custom-scrollbar">

        {/* dirección */}
        {(hasDireccion || showNota) && (
          <div className="flex justify-between mt-4">
            {hasDireccion && (
              <p className="text-[13px] md:text-base flex items-start">
                <i className="material-icons-outlined text-primary text-base md:text-xl mr-1">location_on</i>
                <span>{capitalizarPrimeraLetra(get("Domicilios completos"))}</span>
              </p>
            )}
            {showNota && (
              <div
                ref={infoRef}
                className="relative w-[29px] h-7 ml-4"
                onMouseEnter={() => setTooltipVisible(true)}
                onMouseLeave={() => setTooltipVisible(false)}
              >
                <span className="flex items-center justify-center bg-white rounded">
                  <i className="material-icons-outlined text-primary">info</i>
                </span>

                {tooltipVisible && tipPos &&
                  createPortal(
                    <div
                      style={{
                        position: "fixed",
                        left: tipPos.left + 20, // Siempre a la derecha del ícono
                        top: tipPos.top - 10, // Ligeramente arriba del ícono
                        zIndex: 9999,
                        maxWidth: 320,
                        pointerEvents: "none",
                      }}
                      className="bg-white text-primary text-xs rounded px-3 py-2 shadow-md"
                    >
                      {notaUbicacion}
                    </div>,
                    document.body
                  )
                }
              </div>
            )}
          </div>
        )}

        {/* municipio/departamento */}
        {hasMuniDepto && (
          <div className="ml-6 mt-2">
            {!!departamento && (
              <p className="text-xs md:text-sm">
                <strong>Departamento:</strong> {capitalizarPrimeraLetra(departamento)}
              </p>
            )}
            {!!municipio && (
              <p className="text-xs md:text-sm">
                <strong>Municipio:</strong> {capitalizarPrimeraLetra(municipio)}
              </p>
            )}
          </div>
        )}

        {/* poblaciones */}
        {buildPopulation.length > 0 && (
          <div className="flex justify-between items-center mt-6">
            <div
              className={`grid gap-1 p-1 ${
                buildPopulation.length > 1
                  ? "grid-cols-2"
                  : "grid-cols-1"
              }`}
            >
              {buildPopulation.map((population, idx) => (
                <div key={idx} className="flex items-center">
                  <div
                    className="rounded-full w-8 h-8 mr-1 flex items-center justify-center border flex-shrink-0 aspect-square"
                    style={{ backgroundColor: population.color }}
                  >
                    <img
                      src={`/assets/images/${population.icon}`}
                      alt={`${population.label} Icon`}
                      className="w-5 h-5 svg-icon-white"
                    />
                  </div>
                  <p className="text-xs md:text-sm">{population.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* servicios */}
        {buildService.length > 0 && (
          <div className="flex gap-1 flex-wrap my-4 ml-2">
            {buildService.map((service, idx) => (
              <div key={idx} className=" bg-primaryHeader  rounded p-2 w-fit">
                <p className="text-primary text-xs md:text-sm">{service.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* sigep / ciiu / accesibilidad */}
        {(hasSigep || hasCiiu || hasAcc) && (
          <div className="ml-2 mt-6">
            {hasSigep && (
              <p className="text-xs md:text-base"><strong>Cod. SIGEP:</strong> {capitalizarPrimeraLetra(get("COD SIGEP"))}</p>
            )}
            {hasCiiu && (
              <p className="text-xs md:text-base"><strong>Cod. CIIU:</strong> {get("Código CIIU")}</p>
            )}
            {hasAcc && (
              <div className="mt-2 ml-2 items-center">
                <ul className="text-sm font-semibold list-disc">
                  {accSilla && <li>Accesible con silla de ruedas</li>}
                  {accVisual && <li>Accesible para personas con discapacidad visual</li>}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* horarios */}
        {(hasDias || hasHoraIni || hasHoraFin) && (
          <div className="mt-6">
            <div className="flex items-center mb-3">
              <i className="material-icons-outlined text-primary mr-1">access_time</i>
              <p className="text-sm md:text-lg font-bold">Horarios de Atención</p>
            </div>
            <div className="ml-2">
              {hasDias && (
                <div className="flex mb-1 items-center">
                  <p className="text-xs md:text-base"><strong>Días:</strong></p>
                  {dayOfWeek.map((day, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-center rounded-full w-5 h-5 md:w-6 md:h-6 text-center ml-1 ${
                        isDiaDeAtencion(day.dia) ? 'bg-primary text-white' : 'border border-primary'
                      }`}
                    >
                      <p className="text-xs md:text-base">{day.inicial}</p>
                    </div>
                  ))}
                </div>
              )}
              {(hasHoraIni && hasHoraFin) && (
                <p className="text-xs md:text-base">
                  <strong>Hora:</strong> {get("Horario de inicio de la atención")} hs. a {get("Horario de finalización de la atención")} hs.
                </p>
              )}
            </div>
          </div>
        )}

        {/* contacto */}
        {(hasTel || hasEmail || hasWeb) && (
          <div className="mt-6">
            <div className="flex items-center mb-3">
              <i className="material-icons-outlined text-primary mr-1">list</i>
              <p className="text-sm md:text-lg font-bold">Contacto</p>
            </div>
            <div className="ml-2">
              {hasTel && (
                <div className="flex mb-1 items-center">
                  <i className="material-icons-outlined text-primary mr-1 text-lg md:text-2xl">phone</i>
                  <p className="text-xs md:text-base">{get("Teléfono del Centro de Cuidado")}</p>
                </div>
              )}
              {hasEmail && (
                <div className="flex mb-1 items-center">
                  <i className="material-icons-outlined text-primary mr-1 text-lg md:text-2xl">email</i>
                  <p className="text-xs md:text-base">{get("Correo electrónico del Centro de Cuidado")}</p>
                </div>
              )}
              {hasWeb && (
                <div className="flex items-center">
                  <i className="material-icons-outlined text-primary mr-1 text-lg md:text-2xl">public</i>
                  <p className="text-xs md:text-base">{get("Página web del Centro de Cuidado")}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewDetail;
